import React, { useEffect, useState } from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { updateModalContext } from "actions";

import { MasterRuleAction } from "actions/rules";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { Button, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { ModalTypes } from "constants/navigation";

import { convertToExternalRule } from "containers/Settings/RuleBooks/export";
import {
  convertToInternalRule,
  getParentInputDataType,
} from "containers/Settings/RuleBooks/import";
import { RuleForm } from "containers/Settings/RuleBooks/Rules/CustomRules";
import { resolveParentDataType } from "containers/Settings/RuleBooks/Rules/CustomRules/lib";
import { selectPrerequisiteMasterRuleChainByMasterRuleIdLookup } from "containers/Settings/RuleBooks/Schema/livestockSaleSchema";
import {
  RuleFieldSchema,
  useRuleFieldSchema,
} from "containers/Settings/RuleBooks/Schema/schemaContext";

import {
  getContextByModalType,
  getMasterRuleById,
  getMasterRuleSets,
  selectBillingTagOptions,
} from "selectors";

function MasterRuleFooter(props) {
  const { onClose } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  useSubmitHandler(true, setIsSubmitEnabled);

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      <Button type="submit" disabled={!isSubmitEnabled}>
        Save
      </Button>
    </>
  );
}

export function MasterRuleModal(props) {
  const { onClose } = props;

  const dispatch = useDispatch();
  const context =
    useSelector(getContextByModalType(ModalTypes.EditMasterRule)) || {};
  const { masterRuleId, ruleSetId } = context;

  const handleClose = () => {
    dispatch(
      updateModalContext(ModalTypes.EditMasterRule, { ruleSetId: null }),
    );
    onClose();
  };

  const ruleChainLookup = useSelector(
    selectPrerequisiteMasterRuleChainByMasterRuleIdLookup,
  );
  const masterRuleSetByIdLookup = useSelector(getMasterRuleSets);

  useEffect(() => {
    masterRuleId && dispatch(MasterRuleAction.requestOne({ id: masterRuleId }));
  }, [dispatch, masterRuleId]);

  const billingTags = useSelector(selectBillingTagOptions);

  const masterRule = useSelector(getMasterRuleById(masterRuleId)) || null;

  const schema = useRuleFieldSchema();

  function onSubmit(values) {
    const updatedParentDataType = resolveParentDataType(
      values.prerequisite_rule_ids,
      ruleChainLookup,
      values.rule_set_id,
      masterRuleSetByIdLookup,
      schema,
    );
    const externalRule = convertToExternalRule(
      { ...values },
      schema,
      updatedParentDataType,
    );

    dispatch(
      MasterRuleAction.update(externalRule, {
        changeReason: "Updated from Master Rule Modal",
      }),
    );

    handleClose();
  }

  if (!masterRuleId) {
    return null;
  }

  const ruleChain = ruleChainLookup[masterRuleId] || null;
  const parentDataType = getParentInputDataType(
    // The Rule will determine its own data type, it only need to be given the path of the prerequisite rule/rulset
    ruleChain?.inputPathParts.slice(0, -1),
    schema,
  );

  const initialValues = convertToInternalRule(
    { ...masterRule, rule_set_id: ruleSetId || masterRule.rule_set_id },
    schema,
    parentDataType,
  );

  return (
    <Dialog open onClose={handleClose} fullScreen>
      <DialogTitle onClose={handleClose}>
        <AuditLogLink
          auditLogType={AuditLogTypes.MASTER_RULE}
          dataId={masterRuleId}
          returnTo={window.location.hash}
        />
        &nbsp; Edit Master Rule
        <AdminLink
          app="agrinous_rules"
          model="masterrule"
          cbid={masterRuleId}
        />
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        key={masterRuleId}
      >
        <Form>
          <DialogContent>
            <RuleFieldSchema value>
              <RuleForm
                isMasterRule
                billingTags={billingTags}
                ruleId={masterRuleId}
              />
            </RuleFieldSchema>
          </DialogContent>
          <DialogActions>
            <MasterRuleFooter onClose={handleClose} />
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
}
